import { initializeApp, getApps, getApp } from 'firebase/app'
import { Auth, getAuth, connectAuthEmulator } from 'firebase/auth'
import * as Sentry from '@sentry/react'
import { env } from '@/lib/env'

let authPromise: Promise<Auth | null> | null = null

async function initializeFirebaseAuth(): Promise<Auth | null> {
  if (typeof window === 'undefined') return null

  try {
    const apiKey = await env.getFirebaseApiKey()
    const app = getApps().length
      ? getApp()
      : initializeApp({
          apiKey,
          authDomain: env.FIREBASE_AUTH_DOMAIN,
          projectId: env.FIREBASE_PROJECT_ID,
          storageBucket: env.FIREBASE_STORAGE_BUCKET,
          messagingSenderId: env.FIREBASE_MESSAGING_SENDER_ID,
          appId: env.FIREBASE_APP_ID,
        })
    const auth = getAuth(app)

    if (env.FIREBASE_USE_EMULATOR === 'true') {
      const emulatorAuth = auth as Auth & { emulatorConfig?: unknown }
      if (!emulatorAuth.emulatorConfig) {
        connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true })
      }
    }

    return auth
  } catch (error) {
    // Auth is the gateway to the whole app, so an init failure (bad/absent REP
    // config, decryption failure, Firebase outage) is high-signal. Report it as
    // a distinct, searchable issue type with a stable fingerprint so every cause
    // groups under one issue. The API key itself is never attached.
    Sentry.captureException(error, {
      tags: { firebase_init: 'failed' },
      fingerprint: ['firebase-init-failure'],
    })
    throw error
  }
}

export function getFirebaseAuth(): Promise<Auth | null> {
  authPromise ??= initializeFirebaseAuth().catch((error) => {
    authPromise = null
    throw error
  })
  return authPromise
}
